import React, { useMemo, useState } from "react";
import { Modal, Platform, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector } from "react-redux";
import CrossPlatformDropdown from "../../../components/CrossPlatformDropdown";
import { selectOrgTypes } from "../../../store/slices/masterDataSlice";
import AdditionalFieldsModal, { Field } from "./AdditionalFieldsModal";

export interface WizardTertiary {
  name: string;
}

export interface WizardSubcategory {
  name: string;
  noTertiary: boolean;
  tertiaries: WizardTertiary[];
}

export interface CategoryCreationDraft {
  categoryName: string;
  noSubcategories: boolean;
  subcategories: WizardSubcategory[];
  assignments: Record<string, { organization: string; team: string }>;
  fields: Record<string, Field[]>;
}

export interface WizardTeamOption {
  id: number;
  name: string;
}

interface AddCategoryWizardProps {
  visible: boolean;
  onClose: () => void;
  onSave: (draft: CategoryCreationDraft) => void | Promise<void>;
  existingNames?: string[];
  teams?: WizardTeamOption[];
}

type Step = "category" | "subcategories" | "tertiary" | "review";
type ReviewRow = { subcategory: WizardSubcategory; tertiary: WizardTertiary | null };

const emptySubcategory = (): WizardSubcategory => ({
  name: "",
  noTertiary: false,
  tertiaries: [{ name: "" }, { name: "" }],
});

interface CancelConfirmationModalProps {
  visible: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

const CancelConfirmationModal: React.FC<CancelConfirmationModalProps> = ({
  visible,
  onKeepEditing,
  onDiscard,
}) => (
  <Modal visible={visible} transparent animationType="fade" onRequestClose={onKeepEditing}>
    <View style={styles.confirmationOverlay}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onKeepEditing} />
      <View style={styles.confirmationModal}>
        <View style={styles.confirmationHeader}>
          <View style={styles.confirmationHeading}>
            <View style={styles.warningIcon}>
              <MaterialIcons name="warning" size={22} color="#E87500" />
            </View>
            <Text style={styles.confirmationTitle}>Confirmation</Text>
          </View>
          <Pressable onPress={onKeepEditing} style={styles.close}>
            <MaterialIcons name="close" size={28} color="#202020" />
          </Pressable>
        </View>
        <View style={styles.confirmationBody}>
          <Text style={styles.confirmationMessage}>
            Are you sure you want to cancel? All progress will be lost.
          </Text>
        </View>
        <View style={styles.confirmationFooter}>
          <Pressable style={styles.confirmationNoButton} onPress={onKeepEditing}>
            <Text style={styles.backText}>No</Text>
          </Pressable>
          <Pressable style={styles.confirmationYesButton} onPress={onDiscard}>
            <Text style={styles.nextText}>Yes</Text>
          </Pressable>
        </View>
      </View>
    </View>
  </Modal>
);

const AddCategoryWizard: React.FC<AddCategoryWizardProps> = ({
  visible,
  onClose,
  onSave,
  existingNames = [],
  teams = [],
}) => {
  const orgTypes = useSelector(selectOrgTypes);
  const [step, setStep] = useState<Step>("category");
  const [categoryName, setCategoryName] = useState("");
  const [noSubcategories, setNoSubcategories] = useState(false);
  const [subcategories, setSubcategories] = useState<WizardSubcategory[]>([emptySubcategory(), emptySubcategory()]);
  const [subcategoryIndex, setSubcategoryIndex] = useState(0);
  const [assignments, setAssignments] = useState<CategoryCreationDraft["assignments"]>({});
  const [fields, setFields] = useState<CategoryCreationDraft["fields"]>({});
  const [fieldEditorKey, setFieldEditorKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const reset = () => {
    setStep("category");
    setCategoryName("");
    setNoSubcategories(false);
    setSubcategories([emptySubcategory(), emptySubcategory()]);
    setSubcategoryIndex(0);
    setAssignments({});
    setFields({});
    setFieldEditorKey(null);
    setSaving(false);
    setShowDiscardConfirm(false);
  };

  const discard = () => {
    reset();
    onClose();
  };

  const close = () => setShowDiscardConfirm(true);

  const duplicateCategory = Boolean(categoryName.trim()) && existingNames.some(
    (name) => name.trim().toLowerCase() === categoryName.trim().toLowerCase()
  );
  const validSubcategories = useMemo(
    () => subcategories.filter((subcategory) => subcategory.name.trim()),
    [subcategories]
  );
  const currentSubcategory = subcategories[subcategoryIndex];
  const reviewRows = useMemo<ReviewRow[]>(
    () => validSubcategories.flatMap<ReviewRow>((subcategory): ReviewRow[] => {
      const rows: ReviewRow[] = [{ subcategory, tertiary: null }];
      if (!subcategory.noTertiary) {
        rows.push(...subcategory.tertiaries
          .filter((tertiary) => tertiary.name.trim())
          .map((tertiary): ReviewRow => ({ subcategory, tertiary })));
      }
      return rows;
    }),
    [validSubcategories]
  );
  const assignmentRows: ReviewRow[] = [{ subcategory: { name: categoryName.trim() } as WizardSubcategory, tertiary: null }, ...reviewRows];

  const updateSubcategory = (index: number, update: Partial<WizardSubcategory>) => {
    setSubcategories((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...update } : item));
  };

  const updateTertiary = (subcategoryIndexToUpdate: number, tertiaryIndex: number, name: string) => {
    setSubcategories((current) => current.map((subcategory, itemIndex) => itemIndex !== subcategoryIndexToUpdate ? subcategory : {
      ...subcategory,
      tertiaries: subcategory.tertiaries.map((tertiary, currentIndex) => currentIndex === tertiaryIndex ? { name } : tertiary),
    }));
  };

  const next = () => {
    if (step === "category") {
      setStep("subcategories");
      return;
    }
    if (step === "subcategories") {
      if (noSubcategories) {
        setStep("review");
        return;
      }
      if (!validSubcategories.length) return;
      setSubcategoryIndex(0);
      setStep("tertiary");
      return;
    }
    if (step === "tertiary") {
      const nextIndex = subcategoryIndex + 1;
      if (nextIndex < validSubcategories.length) {
        setSubcategoryIndex(nextIndex);
      } else {
        setStep("review");
      }
    }
  };

  const back = () => {
    if (step === "subcategories") setStep("category");
    else if (step === "tertiary") {
      if (subcategoryIndex > 0) setSubcategoryIndex((current) => current - 1);
      else setStep("subcategories");
    } else if (step === "review") {
      if (noSubcategories) setStep("subcategories");
      else {
        setSubcategoryIndex(Math.max(validSubcategories.length - 1, 0));
        setStep("tertiary");
      }
    }
  };

  const canNext = step === "category" ? Boolean(categoryName.trim()) && !duplicateCategory :
    step === "subcategories" ? noSubcategories || Boolean(validSubcategories.length) :
    step === "tertiary" ? Boolean(currentSubcategory?.noTertiary || currentSubcategory?.tertiaries.some((tertiary) => tertiary.name.trim())) :
    assignmentRows.every(({ subcategory, tertiary }) => Boolean(assignments[`${subcategory.name}:${tertiary?.name || "category"}`]?.organization && assignments[`${subcategory.name}:${tertiary?.name || "category"}`]?.team));

  const save = async () => {
    if (!canNext || saving) return;
    setSaving(true);
    try {
      await onSave({ categoryName: categoryName.trim(), noSubcategories, subcategories: validSubcategories, assignments, fields });
      reset();
    } finally {
      setSaving(false);
    }
  };

  const setAssignment = (key: string, field: "organization" | "team", value: string) => {
    setAssignments((current) => ({ ...current, [key]: { organization: current[key]?.organization || "", team: current[key]?.team || "", [field]: value } }));
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={close}>
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={close} />
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>{step === "category" ? "Add Ticket Category" : step === "subcategories" ? "Add Subcategories" : step === "tertiary" ? "Add Tertiary Categories" : "Review Category Creation"}</Text>
            <Pressable onPress={close} style={styles.close}><MaterialIcons name="close" size={24} color="#034895" /></Pressable>
          </View>
          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {step === "category" && <View style={styles.field}><Text style={styles.label}>Category Name <Text style={styles.required}>*</Text></Text><View style={styles.itemRow}><TextInput autoFocus style={[styles.input, duplicateCategory && styles.errorInput]} placeholder="e.g. Facilities Request" value={categoryName} onChangeText={setCategoryName} maxLength={60} /><Pressable style={styles.fieldsButton} onPress={() => setFieldEditorKey("category")}><MaterialIcons name="add" size={15} color="#034895" /><Text style={styles.fieldsButtonText}>Add Fields{fields.category?.length ? ` (${fields.category.length})` : ""}</Text></Pressable></View>{duplicateCategory && <Text style={styles.errorText}>Duplicate Category</Text>}</View>}
            {step === "subcategories" && <>
              <Text style={styles.contextLabel}>Category</Text><Text style={styles.contextValue}>{categoryName}</Text>
              <View style={styles.switchRow}><Text style={styles.label}>No Subcategories for this Category</Text><Switch value={noSubcategories} onValueChange={setNoSubcategories} /></View>
              {!noSubcategories && subcategories.map((subcategory, index) => { const key = `subcategory:${index}`; return <View style={styles.itemRow} key={`sub-${index}`}><TextInput style={styles.input} placeholder="Subcategory name" value={subcategory.name} onChangeText={(name) => updateSubcategory(index, { name })} /><Pressable style={styles.fieldsButton} onPress={() => setFieldEditorKey(key)}><MaterialIcons name="add" size={15} color="#034895" /><Text style={styles.fieldsButtonText}>Add Fields{fields[key]?.length ? ` (${fields[key].length})` : ""}</Text></Pressable></View>; })}
              {!noSubcategories && <Pressable style={styles.linkButton} onPress={() => setSubcategories((current) => [...current, emptySubcategory()])}><Text style={styles.linkText}>+ Add Subcategory</Text></Pressable>}
            </>}
            {step === "tertiary" && currentSubcategory && <>
              <Text style={styles.contextLabel}>Category</Text><Text style={styles.contextValue}>{categoryName}</Text><Text style={styles.contextLabel}>Subcategory</Text><Text style={styles.contextValue}>{currentSubcategory.name}</Text>
              <View style={styles.switchRow}><Text style={styles.label}>No Tertiary for this Subcategory</Text><Switch value={currentSubcategory.noTertiary} onValueChange={(value) => updateSubcategory(subcategoryIndex, { noTertiary: value })} /></View>
              {!currentSubcategory.noTertiary && currentSubcategory.tertiaries.map((tertiary, index) => { const key = `tertiary:${subcategoryIndex}:${index}`; return <View style={styles.itemRow} key={`ter-${index}`}><TextInput style={styles.input} placeholder="Tertiary category name" value={tertiary.name} onChangeText={(name) => updateTertiary(subcategoryIndex, index, name)} /><Pressable style={styles.fieldsButton} onPress={() => setFieldEditorKey(key)}><MaterialIcons name="add" size={15} color="#034895" /><Text style={styles.fieldsButtonText}>Add Fields{fields[key]?.length ? ` (${fields[key].length})` : ""}</Text></Pressable></View>; })}
              {!currentSubcategory.noTertiary && <Pressable style={styles.linkButton} onPress={() => updateSubcategory(subcategoryIndex, { tertiaries: [...currentSubcategory.tertiaries, { name: "" }] })}><Text style={styles.linkText}>+ Add Tertiary</Text></Pressable>}
            </>}
            {step === "review" && <>
              <Text style={styles.reviewHelp}>Confirm the Organization and Team Assignment for each new routing combination.</Text>
              <View style={styles.tableHeader}><Text style={styles.tableCategory}>Category</Text><Text style={styles.tableSubcategory}>Subcategory</Text><Text style={styles.tableTertiary}>Tertiary</Text><Text style={styles.tableAssignment}>Organization</Text><Text style={styles.tableAssignment}>Team Assignment</Text></View>
              {assignmentRows.map(({ subcategory, tertiary }) => { const key = `${subcategory.name}:${tertiary?.name || "category"}`; const sourceIndex = subcategories.findIndex((item) => item.name === subcategory.name); const selectedOrganization = assignments[key]?.organization ? { id: assignments[key].organization, displayText: assignments[key].organization } : null; const selectedTeam = assignments[key]?.team ? { id: assignments[key].team, displayText: teams.find((team) => String(team.id) === assignments[key]?.team)?.name || assignments[key].team } : null; return <View style={styles.reviewRow} key={key}><View style={styles.reviewCategoryCell}><Text style={styles.reviewName}>{categoryName}</Text><Pressable onPress={() => setStep("category")}><MaterialIcons name="edit" size={16} color="#034895" /></Pressable></View><View style={styles.reviewSubcategoryCell}>{subcategory.name !== categoryName && <><Text style={styles.reviewName}>{subcategory.name}</Text><Pressable onPress={() => { setSubcategoryIndex(Math.max(sourceIndex, 0)); setStep("subcategories"); }}><MaterialIcons name="edit" size={16} color="#034895" /></Pressable></>}</View><View style={styles.reviewTertiaryCell}>{tertiary ? <><Text style={styles.reviewName}>{tertiary.name}</Text><Pressable onPress={() => { setSubcategoryIndex(Math.max(sourceIndex, 0)); setStep("tertiary"); }}><MaterialIcons name="edit" size={16} color="#034895" /></Pressable></> : <Text style={styles.mutedDash}>—</Text>}</View><CrossPlatformDropdown childList={orgTypes.map((organization) => ({ id: organization, displayText: organization }))} placeholder="Organization *" selection="single" isClearable selectedValue={selectedOrganization} setSelectedValue={(item) => setAssignment(key, "organization", item && !Array.isArray(item) ? String(item.displayText) : "")} size="small" disableSorting style={styles.assignmentDropdown} /><CrossPlatformDropdown childList={teams.map((team) => ({ id: String(team.id), displayText: team.name }))} placeholder="Team Assignment *" selection="single" isClearable selectedValue={selectedTeam} setSelectedValue={(item) => setAssignment(key, "team", item && !Array.isArray(item) ? String(item.id) : "")} size="small" disableSorting style={styles.assignmentDropdown} /></View>; })}
            </>}
          </ScrollView>
          <View style={styles.footer}><Pressable style={styles.cancel} onPress={close}><Text style={styles.cancelText}>Cancel</Text></Pressable>{step !== "category" && <Pressable style={styles.back} onPress={back}><Text style={styles.backText}>Back</Text></Pressable>}<Pressable style={[styles.next, !canNext && styles.disabled]} onPress={step === "review" ? save : next} disabled={!canNext || saving}><Text style={styles.nextText}>{step === "review" ? (saving ? "Saving..." : "Save") : "Next"}</Text></Pressable></View>
          <CancelConfirmationModal visible={showDiscardConfirm} onKeepEditing={() => setShowDiscardConfirm(false)} onDiscard={discard} />
          <AdditionalFieldsModal visible={fieldEditorKey !== null} onClose={() => setFieldEditorKey(null)} onSave={(savedFields) => { if (fieldEditorKey) setFields((current) => ({ ...current, [fieldEditorKey]: savedFields })); setFieldEditorKey(null); }} initialFields={fieldEditorKey ? fields[fieldEditorKey] || [] : []} subtitle="Category creation" />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: "rgba(20, 30, 50, 0.45)", justifyContent: "center", alignItems: "center" },
  modal: { backgroundColor: "#fff", borderRadius: 8, width: "min(640px, 92%)", maxHeight: "86%", ...Platform.select({ web: { boxShadow: "0 8px 24px rgba(20, 30, 50, 0.16)" } as any, default: { elevation: 8 } }) },
  confirmationOverlay: { flex: 1, backgroundColor: "rgba(20, 30, 50, 0.45)", justifyContent: "center", alignItems: "center", padding: 20 },
  confirmationModal: { backgroundColor: "#fff", borderRadius: 8, width: "min(360px, 92%)", ...Platform.select({ web: { boxShadow: "0 8px 24px rgba(20, 30, 50, 0.2)" } as any, default: { elevation: 8 } }) },
  confirmationHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: "#E3E5E8" },
  confirmationHeading: { flexDirection: "row", alignItems: "center", gap: 10 },
  warningIcon: { width: 32, height: 32, borderRadius: 16, backgroundColor: "#FFE8D2", alignItems: "center", justifyContent: "center" },
  confirmationTitle: { fontSize: 18, fontWeight: "700", color: "#162235" },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingHorizontal: 24, paddingVertical: 18, borderBottomWidth: 1, borderBottomColor: "#F0F0F0" },
  title: { fontSize: 18, fontWeight: "700", color: "#2D2D2D" }, close: { padding: 4 },
  confirmationBody: { paddingHorizontal: 16, paddingVertical: 18 }, confirmationMessage: { fontSize: 14, lineHeight: 20, color: "#424B59", textAlign: "left" },
  confirmationFooter: { flexDirection: "row", justifyContent: "flex-end", alignItems: "center", gap: 8, paddingHorizontal: 14, paddingVertical: 12, borderTopWidth: 1, borderTopColor: "#E3E5E8" },
  confirmationNoButton: { minWidth: 90, alignItems: "center", paddingHorizontal: 16, paddingVertical: 8, borderWidth: 2, borderColor: "#D0D4D9", borderRadius: 100 },
  confirmationYesButton: { minWidth: 90, alignItems: "center", paddingHorizontal: 16, paddingVertical: 8, borderRadius: 100, backgroundColor: "#075DB5" },
  body: { paddingHorizontal: 24, maxHeight: Platform.OS === "web" ? ("58vh" as unknown as number) : 500 } as any, bodyContent: { paddingVertical: 16, gap: 12 },
  field: { gap: 6 }, label: { fontSize: 12, fontWeight: "600", color: "#585858" }, required: { color: "#D32F2F" },
  input: { flex: 1, height: 40, borderWidth: 1, borderColor: "#D9D9D9", borderRadius: 6, paddingHorizontal: 12, fontSize: 14, color: "#2D2D2D" }, errorInput: { borderColor: "#D32F2F" }, errorText: { color: "#D32F2F", fontSize: 12 },
  contextLabel: { fontSize: 11, color: "#818181", marginTop: 4 }, contextValue: { fontSize: 15, fontWeight: "700", color: "#333" },
  switchRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", borderTopWidth: 1, borderBottomWidth: 1, borderColor: "#F0F0F0", paddingVertical: 10 },
  itemRow: { flexDirection: "row", alignItems: "center", gap: 8 }, fieldsButton: { flexDirection: "row", alignItems: "center", gap: 3, paddingHorizontal: 8, paddingVertical: 8 }, fieldsButtonText: { color: "#034895", fontSize: 12, fontWeight: "700" }, linkButton: { alignSelf: "flex-start", paddingVertical: 4 }, linkText: { color: "#034895", fontSize: 13, fontWeight: "700" },
  reviewHelp: { fontSize: 11, color: "#818181", lineHeight: 16, marginBottom: 2 }, tableHeader: { flexDirection: "row", borderBottomWidth: 1, borderColor: "#D9D9D9", paddingBottom: 8, gap: 6 }, tableCategory: { flex: 1.1, fontSize: 11, fontWeight: "700", color: "#585858" }, tableSubcategory: { flex: 1.1, fontSize: 11, fontWeight: "700", color: "#585858" }, tableTertiary: { flex: 1.1, fontSize: 11, fontWeight: "700", color: "#585858" }, tableAssignment: { flex: 1.2, fontSize: 11, fontWeight: "700", color: "#585858" }, reviewRow: { flexDirection: "row", alignItems: "center", gap: 6, borderBottomWidth: 1, borderColor: "#F0F0F0", paddingVertical: 8 }, reviewCategoryCell: { flex: 1.1, flexDirection: "row", alignItems: "center", gap: 3 }, reviewSubcategoryCell: { flex: 1.1, flexDirection: "row", alignItems: "center", gap: 3 }, reviewTertiaryCell: { flex: 1.1, flexDirection: "row", alignItems: "center", gap: 3 }, reviewName: { flex: 1, fontSize: 12, color: "#034895" }, mutedDash: { color: "#D0D0D0", fontSize: 14 }, assignmentDropdown: { flex: 1.2, minWidth: 0 },
  footer: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 8, paddingHorizontal: 24, paddingVertical: 12, borderTopWidth: 1, borderTopColor: "#F0F0F0" }, cancel: { padding: 10 }, cancelText: { color: "#585858", fontWeight: "700" }, back: { paddingHorizontal: 20, paddingVertical: 9, borderWidth: 1, borderColor: "#D9D9D9", borderRadius: 100 }, backText: { color: "#034895", fontWeight: "700" }, next: { backgroundColor: "#034895", paddingHorizontal: 24, paddingVertical: 9, borderRadius: 100 }, disabled: { opacity: 0.5 }, nextText: { color: "#fff", fontWeight: "700" },
});

export default AddCategoryWizard;